import React from "react"
import type { WindowLocation } from "@reach/router"
import { useLocation } from "@reach/router"

import { useStepOutfitter } from "~components/self-onboard/steps/outfitter-setup/setup-helpers"
import AuthenticatingLoader from "~components/shared/loader/authenticating-loader"
import Redirect from "~components/shared/redirect"
import { ROUTES } from "~utils/helpers/navigation-helpers"
import { useUser } from "~contexts/user-provider"
import { isOutfitterSelfOnboardingFinished } from "~utils/helpers/outfitter-helpers"
import type { PortalTypeForContext } from "~contexts/outfitter-provider"
import { usePortalEntitiesContext } from "~contexts/outfitter-provider"
import { useOnboardingEventOrganizer } from "~utils/hooks/use-onboarding-event-organizer"
import {
    EventOrganizerSelfOnboardingStepType,
    PortalType,
} from "~graphql/generated/types"

export default function SelfOnboardRedirect({
    children,
}: {
    readonly children: React.ReactNode
}) {
    const {
        shouldRedirectToOutfitterOnboarding,
        shouldRedirectToEventOrganizerOnboarding,
        isLoading,
    } = useShouldRedirectToOnboarding()

    if (isLoading) {
        return <AuthenticatingLoader />
    }

    if (shouldRedirectToOutfitterOnboarding) {
        return <Redirect to={ROUTES.SELF_ONBOARD} />
    }

    if (shouldRedirectToEventOrganizerOnboarding) {
        return <Redirect to={ROUTES.EVENT_ORGANIZER_SELF_ONBOARD} />
    }

    // we need to wrap children in a fragment to avoid SSR issues
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>
}

function useShouldRedirectToOnboarding() {
    const location = useLocation()
    const { portalType } = usePortalEntitiesContext()
    const { isAdminNotImpersonating } = useUser()

    const { shouldRedirectToOutfitterOnboarding, isOutfitterLoading } =
        useShouldRedirectToOutfitterOnboarding({
            portalType,
            isAdminNotImpersonating,
            location,
        })
    const {
        shouldRedirectToEventOrganizerOnboarding,
        isEventOrganizerLoading,
    } = useShouldRedirectToEventOrganizerOnboarding({
        portalType,
        isAdminNotImpersonating,
        location,
    })
    const isLoading = isOutfitterLoading || isEventOrganizerLoading

    return {
        shouldRedirectToOutfitterOnboarding,
        shouldRedirectToEventOrganizerOnboarding,
        isLoading,
    }
}

function useShouldRedirectToOutfitterOnboarding({
    portalType,
    isAdminNotImpersonating,
    location,
}: {
    portalType: PortalTypeForContext
    isAdminNotImpersonating: boolean
    location: WindowLocation
}) {
    const { outfitter, isOutfitterLoading } = useStepOutfitter()

    const isOutfitterPortal = portalType === PortalType.Outfitter
    const isFinished = isOutfitterSelfOnboardingFinished(outfitter)

    const shouldRedirectToOutfitterOnboarding =
        outfitter &&
        isOutfitterPortal &&
        !isFinished &&
        // users who exited the onboarding flow should not be forced back into it
        !outfitter.is_onboarding_flow_skipped &&
        // need to check current path otherwise there can be a state where loading is done and outfitter is still null so it re-directs infinite times
        !location.pathname.includes(ROUTES.SELF_ONBOARD) &&
        !location.pathname.includes(ROUTES.TEAM_MEMBER_ONBOARDING) &&
        // only force redirect for admins that are not impersonating
        !isAdminNotImpersonating

    return { shouldRedirectToOutfitterOnboarding, isOutfitterLoading }
}

function useShouldRedirectToEventOrganizerOnboarding({
    portalType,
    isAdminNotImpersonating,
    location,
}: {
    portalType: PortalTypeForContext
    isAdminNotImpersonating: boolean
    location: WindowLocation
}) {
    const { eventOrganizer, isEventOrganizerLoading } =
        useOnboardingEventOrganizer()

    const isEventOrganizerPortal = portalType === PortalType.RegionalDirector
    const isFinished =
        eventOrganizer?.current_self_onboard_step ===
        EventOrganizerSelfOnboardingStepType.Finished

    const shouldRedirectToEventOrganizerOnboarding =
        eventOrganizer &&
        isEventOrganizerPortal &&
        !isFinished &&
        !location.pathname.includes(ROUTES.EVENT_ORGANIZER_SELF_ONBOARD) &&
        !location.pathname.includes(ROUTES.TEAM_MEMBER_ONBOARDING) &&
        !isAdminNotImpersonating

    return { shouldRedirectToEventOrganizerOnboarding, isEventOrganizerLoading }
}
